import React, { useState, useEffect, useRef } from "react";
import { Panel } from "primereact/panel";
import DropDownHelper from "../../utilities/DropDownHelper";
import StringHelper from "../../utilities/StringHelper";
import Consts from "../../utilities/Consts";
import * as NetworkHelper from "../../utilities/Networkhelper";
import { BlockUI } from "primereact/blockui";
import CommonHelper from "../../utilities/CommonHelper";
import { Button } from "primereact/button";
import { useStateValue } from "../../utilities/StateProvider";
import { ProgressBar } from "primereact/progressbar";
import { createReportViewer } from "../../utilities/StimulsoftHelper";

/**
 * Bulk invoice print/preview.
 * NOTE: This component intentionally ONLY renders report/FaturaBaski.mrt (no Gonderim/Banka/Taksit reports).
 * It mirrors the exact reportDataFatura preparation logic from FaturaBaskiViewReport.js.
 */
export const FaturaBaskiBulkViewReport = (props) => {
  const { siparisler, onClose } = props;
  const cLabels = Consts.localizedStrings;
  const [{ firma_bilgileri, userLanguage, isAdmin }] = useStateValue();

  const [blocked, setBlocked] = useState(false);
  const [pbValue, setpbValue] = useState(0);
  const isFirstRender = useRef(true);

  // Ref persists the report object through re-renders caused by setpbValue
  const report_merge = useRef(new window.Stimulsoft.Report.StiReport()).current;

  // Mirrors FaturaBaskiViewReport.js defaults
  const _reportDataFatura = {
    isk_t_visible: true,
    skonto_visible: true,
    ip_tar: "",
    ip_tar_visible: false,
    ara_toplam_visible: true,
    xrTableCell13_visible: true,
    xrPictureBox_visible: false,
    iptal_neden_visible: false,
    iptal_aciklama_visible: false,
    odeme_tar_visible: false,
    xrPictureBox3_visible: false,
    xrLabel8_visible: false,
    xrTableCell11_visible: false,
    gecikme_visible: false,
    sip_tar: "",
    xrTableCell12: "Summe :",
    skonto: "Skonto :",
    xrTableCell13: "Zwischensumme :",
    xrTableCell15: "Versandkosten :",
    gecikme: 0,
    odeme_tar: "",
    is_gutschrift: false
  };

  // optional flags (same names as single view component)
  const ghsft_on = StringHelper.isNotEmpty(props.ghsft_on) ? props.ghsft_on : 0;
  const uyarili = StringHelper.isNotEmpty(props.uyarili) ? props.uyarili : 0;

  // cache dropdown lists to avoid refetching per invoice
  const dropdownCacheRef = useRef({ unvanlar: null, sys_fat_iptal: null });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      generateBulkReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const template = (options) => {
    const className = `${options.className} p-jc-start`;
    const titleClassName = `${options.titleClassName} p-pl-1`;

    return (
      <div className={className}>
        <span className={titleClassName}>{cLabels.siparis}</span>
        <Button
          icon="pi pi-times"
          className="p-button-rounded p-button-danger"
          onClick={() => (onClose ? onClose() : props.onCloseSidebar?.())}
        />
      </div>
    );
  };

  const generateBulkReport = async () => {
    try {
      setBlocked(true);
      window.Stimulsoft.Base.Localization.StiLocalization.setLocalizationFile("report/" + userLanguage + ".xml");
      const viewer = createReportViewer(isAdmin);

      report_merge.render();
      report_merge.renderedPages.clear();

      // Bulk API call for all selected IDs
      const ids = siparisler.map((s) => s.id);
      const response = await NetworkHelper.call_web_service(Consts.network.siparisler.fatura_report_data_list, { ids });

      if (response?.status?.code !== 1) throw new Error("Fetch failed");

      const allData = response.datalist || [];
      for (let i = 0; i < allData.length; i++) {
        setpbValue(Math.round(((i + 1) * 100) / allData.length));
        await processAndRenderInvoice(allData[i]);
      }

      viewer.report = report_merge;
      viewer.renderHtml("viewer");
    } catch (error) {
      console.error("Bulk Generation Error:", error);
      CommonHelper.handleCatch(error);
    } finally {
      setBlocked(false);
    }
  };

  const getUnvanlar = async () => {
    if (!dropdownCacheRef.current.unvanlar) {
      dropdownCacheRef.current.unvanlar = await DropDownHelper.unvan();
    }
    return dropdownCacheRef.current.unvanlar;
  };

  const getSysFatIptal = async () => {
    if (!dropdownCacheRef.current.sys_fat_iptal) {
      dropdownCacheRef.current.sys_fat_iptal = await DropDownHelper.sys_fat_iptal();
    }
    return dropdownCacheRef.current.sys_fat_iptal;
  };

  const processAndRenderInvoice = async (dataItem) => {
    try {
      const { siparis, customer, icerik } = dataItem || {};
      const countryCode = siparis?.country_code;

      let vadelimi = 0;
      let banka_baski_ok_last = 0; // kept for exact logic parity (even though bulk doesn't print banka report)

      let reportDataFatura = { ..._reportDataFatura, ...(siparis || {}) };

      // siparis icerik
      reportDataFatura.siparis_icerik = CommonHelper.formatArrayCurrency(icerik || [], ["stk_birim", "stk_toplam"], countryCode);

      // unvan
      const unvanlar = await getUnvanlar();
      const unvan = StringHelper.isEmpty(siparis?.cus_m_f) ? "" : await DropDownHelper.getLabelByKeyAsync(unvanlar, siparis.cus_m_f);
      reportDataFatura.unvan = unvan;

      // odeme gun
      let odeme_gun = CommonHelper.dateDiffDays(siparis?.s_odeme_tarihi, siparis?.f_tarih);
      if (odeme_gun == 0) odeme_gun = 14;

      // sip_tar
      reportDataFatura.sip_tar = CommonHelper.getFormatDateDDMMYYYY(siparis?.s_tar);

      // CH/TR firma bloklari
      let f_b1 = "";
      let f_b2 = "";
      let f_b3 = "";
      if ("CH" == countryCode) {
        f_b1 = firma_bilgileri.ch_contact_info;
        f_b2 = firma_bilgileri.ch_address_info;
        f_b3 = firma_bilgileri.ch_bank_info;
      } else {
        f_b1 = firma_bilgileri.k1_1;
        f_b2 = firma_bilgileri.k2_1;
        f_b3 = firma_bilgileri.k3_1;
      }

      // iptal / kapali fatura
      if (siparis?.["sd_cancel_on"] == "1") {
        const sys_fat_iptal = await getSysFatIptal();
        const iptal_neden = await DropDownHelper.getLabelByKeyAsync(sys_fat_iptal, siparis.f_ip_neden_type);

        reportDataFatura.ip_tar = CommonHelper.getFormatDateDDMMYYYY(siparis["sd_iptal_tar"]);
        reportDataFatura.ip_tar_visible = true;
        reportDataFatura.xrPictureBox_visible = true;
        reportDataFatura.iptal_neden_visible = true;
        reportDataFatura.iptal_aciklama_visible = true;
        reportDataFatura.iptal_neden = iptal_neden;
        reportDataFatura.iptal_aciklama = StringHelper.getNotNullString(siparis["f_ip_aciklama"]);
      }
      if (siparis?.["f_close"] == "1" && siparis?.["sd_cancel_on"] == "0") {
        reportDataFatura.odeme_tar = CommonHelper.getFormatDateDDMMYYYY(siparis["f_odeme_tar"]);
        reportDataFatura.odeme_tar_visible = true;
        reportDataFatura.xrPictureBox3_visible = true;
      }

      // musteri bilgi
      let musteri_bilgi = "";
      if (siparis?.["cus_is_firm"] == "1") {
        musteri_bilgi = StringHelper.getNotNullString(siparis["cus_firm"]) + "\r\n";
      }
      musteri_bilgi = StringHelper.concat(musteri_bilgi, [unvan, siparis?.cus_name, siparis?.cus_surname], true);
      musteri_bilgi = StringHelper.concat(musteri_bilgi, [siparis?.f_adres], true);
      musteri_bilgi = StringHelper.concat(musteri_bilgi, [siparis?.f_pk, siparis?.f_bolge, siparis?.f_sehir], true);
      musteri_bilgi = StringHelper.concat(musteri_bilgi, [siparis?.f_ulke], true);

      reportDataFatura.personel = StringHelper.getNotNullString(siparis?.["per_name"]);
      reportDataFatura.cus_id = siparis?.["cus_id"];
      reportDataFatura.f_tarih = CommonHelper.getFormatDateDDMMYYYY(siparis?.["f_tarih"]);

      // tutar formatlari
      reportDataFatura.aratop = CommonHelper.toFixedCurrencyForReport(siparis?.["s_toplam"], countryCode);
      reportDataFatura.isk_t = CommonHelper.toFixedCurrencyForReport(siparis?.["s_iskonto"], countryCode);
      reportDataFatura.ara_toplam = CommonHelper.toFixedCurrencyForReport(siparis?.["s_ara_toplam"], countryCode);
      reportDataFatura.kargo = CommonHelper.toFixedCurrencyForReport(siparis?.["s_kargo"], countryCode);
      reportDataFatura.genel_t = CommonHelper.toFixedCurrency(siparis?.["s_g_toplam"], undefined, undefined, countryCode);

      vadelimi = CommonHelper.parseFloat(siparis?.["s_vadeli_odeme"]);

      // iskonto yoksa gorunurlukler
      if (CommonHelper.parseFloat(siparis?.["s_iskonto"]) == 0) {
        reportDataFatura.isk_t_visible = false;
        reportDataFatura.skonto_visible = false;
        reportDataFatura.xrTableCell13_visible = false;
        reportDataFatura.ara_toplam_visible = false;
        reportDataFatura.xrTableCell12 = "Zwischensumme :";
      }

      // odeme tarih hesap
      const f_tarih = CommonHelper.str2DateYYYYMMDDHHMM(siparis?.["f_tarih"]);
      const f_tarih_d = CommonHelper.addCurrentDate(f_tarih, undefined, odeme_gun);
      const odeme_tarih = CommonHelper.getFormatDateDDMMYYYY(f_tarih_d);

      // fatura tip
      if (ghsft_on == 1) {
        reportDataFatura.is_gutschrift = true;
        reportDataFatura.fatura_tip = "Gutschrift Nr. " + siparis?.["f_no"];
      } else if (CommonHelper.parseFloat(siparis?.["s_prasentrechnung"]) == 1) {
        reportDataFatura.fatura_tip = "Rechnung Nr. " + siparis?.["f_no"] + "         Präsentrechnung ";
      } else {
        reportDataFatura.fatura_tip = "Rechnung Nr. " + siparis?.["f_no"];
      }

      // kdv / vergisiz / odeme metinleri (exact parity)
      reportDataFatura.vergisiz = "";
      if (ghsft_on == 0) {
        if (CommonHelper.parseFloat(siparis?.["s_vergisiz"]) == 1) {
          reportDataFatura.kdv_1 = "";

          if (CommonHelper.parseFloat(siparis?.["s_lastschrift"]) == 1) {
            banka_baski_ok_last = 1;

            if (CommonHelper.parseFloat(siparis?.["s_last_y"]) > 0) {
              reportDataFatura.skonto = "Skonto (%" + CommonHelper.parseFloat(siparis?.["s_last_y"]) + ") :";
            } else {
              reportDataFatura.skonto = "Skonto :";
            }
            reportDataFatura.vergisiz = reportDataFatura.vergisiz + "Wir buchen den Betrag von Ihrem Konto ab.";
          } else {
            if (CommonHelper.parseFloat(siparis?.["s_gut_shift"]) == 1) {
              reportDataFatura.skonto = "Gutschrift :";
            } else {
              if (CommonHelper.parseFloat(siparis?.["s_man_y"]) > 0) {
                if (CommonHelper.parseFloat(siparis?.["s_ikt_m"]) > 0) {
                  reportDataFatura.skonto = "Rabatt :";
                } else {
                  reportDataFatura.skonto = "Rabatt (%" + CommonHelper.parseFloat(siparis?.["s_man_y"]) + ") :";
                }
              } else {
                reportDataFatura.skonto = "Rabatt :";
              }
            }

            if (CommonHelper.parseFloat(siparis?.["s_vadeli_odeme"]) == 1) {
              reportDataFatura.vergisiz =
                reportDataFatura.vergisiz +
                "Wir bieten Ihnen an, den Gesamtrechnungsbetrag in Höhe von " +
                CommonHelper.toFixedCurrency(siparis?.["s_g_toplam"], undefined, undefined, countryCode) +
                " in " +
                siparis?.["s_taksit_sayisi"] +
                " Raten zu zahlen.\r\n";
              reportDataFatura.vergisiz = reportDataFatura.vergisiz + "Bitte beachten Sie aber, dass der gesamte Restbetrag sofort fällig wird, \r\n";
              reportDataFatura.vergisiz = reportDataFatura.vergisiz + "wenn Sie eine Rate nicht termingerecht zahlen sollten.\r\n\r\n";
              reportDataFatura.vergisiz = reportDataFatura.vergisiz + "\r\nWir wünschen Ihnen viel Freude beim Degustieren.\r\n\r\nIhr Vin-Royal Team";
              vadelimi = 1;
              if (CommonHelper.parseFloat(siparis?.["s_lastschrift"]) == 1) {
                banka_baski_ok_last = 1;
              }
            } else {
              reportDataFatura.vergisiz =
                reportDataFatura.vergisiz + "Wir freuen uns über Zahlung bis Spätestens " + odeme_gun + " Tage (" + odeme_tarih + ") nach Erhalt der Rechnung ohne Abzug.\r\n";
            }
          }

          reportDataFatura.vergisiz = reportDataFatura.vergisiz + "\r\nUID-Nr. Leistungsempfänger: ATU " + siparis?.["cus_atu_no"] + "\r\n";
          reportDataFatura.vergisiz = reportDataFatura.vergisiz + "Innergemeinschaftliche Lieferung steuerfrei gem. Art. 6 Abs. 1 UStG\r\n";
          reportDataFatura.vergisiz = reportDataFatura.vergisiz + "Die Erwerber sind für die Versteuerung in Ihrem Heimatland zuständig.\r\n\r\n";
          reportDataFatura.vergisiz = reportDataFatura.vergisiz + "\r\nWir wünschen Ihnen viel Freude beim Degustieren.\r\n\r\nIhr Vin-Royal Team";
        } else {
          reportDataFatura.kdv_1 =
            "Die Gesamtsumme enthält " +
            siparis?.["kdv_oran"] +
            " % Umsatzsteuer in  Höhe von  " +
            CommonHelper.toFixedCurrency(siparis?.["s_kdv"], undefined, undefined, countryCode) +
            " .\r\nDies entspricht einem Nettobetrag von  " +
            CommonHelper.toFixedCurrency((CommonHelper.parseFloat(siparis?.["s_g_toplam"]) / (100 + CommonHelper.parseFloat(siparis?.["kdv_oran"]))) * 100, undefined, undefined, countryCode) +
            ".";

          if (uyarili == 1) {
            const resp_cari_islem = await NetworkHelper.call_web_service(Consts.network.cari_islem.list, {
              s_no: siparis?.id,
              islem_type: "2",
              in_out: "2",
              sum_field: "borc"
            });

            const odeme_toplam = CommonHelper.parseFloat(resp_cari_islem?.sum);
            reportDataFatura.xrTableCell11_visible = true;
            reportDataFatura.gecikme_visible = true;
            reportDataFatura.kdv_1 =
              reportDataFatura.kdv_1 + "\r\nWir erhielten bisher eine Teilzahlung von: " + CommonHelper.toFixedCurrency(odeme_toplam, undefined, undefined, countryCode);
            reportDataFatura.gecikme = CommonHelper.toFixedCurrency(CommonHelper.parseFloat(siparis?.["s_g_toplam"]) - odeme_toplam, undefined, undefined, countryCode);
            reportDataFatura.xrLabel8_visible = true;
          }

          if (CommonHelper.parseFloat(siparis?.["s_vadeli_odeme"]) == 1) {
            reportDataFatura.vergisiz =
              reportDataFatura.vergisiz +
              "\r\nWir bieten Ihnen an, den Gesamtrechnungsbetrag in Höhe von " +
              CommonHelper.toFixedCurrency(siparis?.["s_g_toplam"], undefined, undefined, countryCode) +
              " in " +
              siparis?.["s_taksit_sayisi"] +
              " Raten zu zahlen.\r\n";
            reportDataFatura.vergisiz = reportDataFatura.vergisiz + "Bitte beachten Sie aber, dass der gesamte Restbetrag sofort fällig wird, \r\n";
            reportDataFatura.vergisiz = reportDataFatura.vergisiz + "wenn Sie eine Rate nicht termingerecht zahlen sollten.\r\n\r\n";
            reportDataFatura.vergisiz = reportDataFatura.vergisiz + "\r\nWir wünschen Ihnen viel Freude beim Degustieren.\r\n\r\n\r\nIhr Vin-Royal Team";
            vadelimi = 1;
            if (CommonHelper.parseFloat(siparis?.["s_lastschrift"]) == 1) {
              banka_baski_ok_last = 1;
            }
          } else {
            if (CommonHelper.parseFloat(siparis?.["s_lastschrift"]) == 1) {
              banka_baski_ok_last = 1;

              if (CommonHelper.parseFloat(siparis?.["s_last_y"]) > 0) {
                reportDataFatura.skonto = "Skonto (%" + CommonHelper.parseFloat(siparis?.["s_last_y"]) + ") :";
              } else {
                reportDataFatura.skonto = "Skonto :";
              }

              reportDataFatura.vergisiz = reportDataFatura.vergisiz + "Wir buchen den Betrag  von Ihrem Konto ab.\r\n\r\n";
              reportDataFatura.vergisiz = reportDataFatura.vergisiz + "Wir wünschen Ihnen viel Freude beim Degustieren.\r\n\r\n\r\n\r\nIhr Vin-Royal Team";
            } else {
              if (CommonHelper.parseFloat(siparis?.["s_gut_shift"]) == 1) {
                reportDataFatura.skonto = "Gutschrift :";
              } else {
                if (CommonHelper.parseFloat(siparis?.["s_man_y"]) > 0) {
                  if (CommonHelper.parseFloat(siparis?.["s_ikt_m"]) > 0) {
                    reportDataFatura.skonto = "Rabatt :";
                  } else {
                    reportDataFatura.skonto = "Rabatt (%" + CommonHelper.parseFloat(siparis?.["s_man_y"]) + ") :";
                  }
                } else {
                  reportDataFatura.skonto = "Rabatt :";
                }
              }
              reportDataFatura.vergisiz =
                reportDataFatura.vergisiz + "Wir freuen uns über Zahlung bis Spätestens " + odeme_gun + " Tage (" + odeme_tarih + ") nach Erhalt der Rechnung ohne Abzug.\r\n";
              reportDataFatura.vergisiz = reportDataFatura.vergisiz + "\r\nWir wünschen Ihnen viel Freude beim Degustieren.\r\n\r\n\r\n\r\nIhr Vin-Royal Team";
            }
          }
        }
      } else {
        reportDataFatura.vergisiz = "\r\nMit freundlichen Grüßen,\r\n\r\nIhr Vin-Royal Team";
      }

      // common fields
      reportDataFatura.musteri_bilgi = musteri_bilgi;
      reportDataFatura.ust_bilgi = firma_bilgileri.ust_bilgi;
      reportDataFatura.f_b1 = f_b1;
      reportDataFatura.f_b2 = f_b2;
      reportDataFatura.f_b3 = f_b3;
      reportDataFatura.firma_ustid = firma_bilgileri.ustid;
      reportDataFatura.firma_steuer = firma_bilgileri.steuer;

      // country code chars (used in some templates)
      if (StringHelper.isNotEmpty(countryCode) && countryCode.length >= 2) {
        reportDataFatura.county_code_char1 = countryCode[0];
        reportDataFatura.county_code_char2 = countryCode[1];
      }

      // Render ONLY invoice report
      const report_fatura = new window.Stimulsoft.Report.StiReport();
      report_fatura.loadFile("report/FaturaBaski.mrt");

      const dataSet = new window.Stimulsoft.System.Data.DataSet("Datta");
      dataSet.readJson(reportDataFatura);

      // CRITICAL: Clear dictionary to prevent data bleeding between orders
      report_fatura.dictionary.databases.clear();
      report_fatura.regData("Demo", "Demo", dataSet);

      await new Promise((resolve) => {
        report_fatura.renderAsync(() => {
          for (let index = 0; index < report_fatura.renderedPages.count; ++index) {
            report_merge.renderedPages.add(report_fatura.renderedPages.getByIndex(index));
          }
          resolve();
        });
      });
    } catch (error) {
      console.error("processAndRenderInvoice error:", error);
      // continue other invoices; do not throw
    }
  };

  return (
    <Panel toggleable headerTemplate={template}>
      <BlockUI blocked={blocked} fullScreen />
      {blocked && <h5>{cLabels.islem_tamamlanma_yuzdesi}</h5>}
      {blocked && <ProgressBar value={pbValue}></ProgressBar>}
      <div id="viewer"></div>
    </Panel>
  );
};
